import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { find, chain } from 'lodash';
import { NavigationLink } from '../../navigation';
import { Link, Settings, UrlBuilder } from '../../core';
import { Site } from '../../sites';
import { Category } from '../../documents';
import { InvestigatorUnreadReportsStorage } from '../investigator-unread-reports-storage';
import { InvestigatorDocumentsCounter } from '../../documents/models/documents-counter';
import { routeData } from '../../shared/route-helpers';
import { InvestigatorEQueriesCountersStorage } from '../e-queries/investigator-e-queries-counters-storage';
import { EQueryCounter } from '../../e-queries/models/e-query-counter';

@Component({
    selector: 'investigator-nav',
    template: require('./investigator-navigation.component.html'),
    styles: [require('./investigator-navigation.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvestigatorNavigationComponent {

    public topLevelMenu: Observable<NavigationLink[]>;
    public ereqLink: Link;

    constructor(
        private route: ActivatedRoute,
        private settings: Settings,
        private unreadReportsRegistry: InvestigatorUnreadReportsStorage,
        private eQueriesCountersStorage: InvestigatorEQueriesCountersStorage,
        private urlBuilder: UrlBuilder) {
        this.topLevelMenu = this.route.data.pipe(map(this.createMenu));
        this.ereqLink = this.settings.linksDictionary['erequisition'];
    }

    public onSiteSelectionChange(site: Site) {
        this.urlBuilder
            .esa()
            .site(site)
            .navigate({ preserveFragments: true });
    }

    private createMenu = (data: Data) => {
        //add state dependent logic

        const selectedSite: Site = data[routeData.selectedSite];
        const siteSpecificCategories: Category[] = data[routeData.siteSpecificCategories];

        const subjectsUrl = this.urlBuilder.esa().site(selectedSite).subject().url();
        const subjectsLink = new NavigationLink('Subjects', of(subjectsUrl), 'people');
        const labReportsLink = this.getLabReportsLink(selectedSite, siteSpecificCategories);
        const documentsLinks = this.getDocumentsLinks(selectedSite, siteSpecificCategories);
        const onScreenResultsLinks = this.getOnScreenResultsLinks(selectedSite);
        const kitInventoryLink = this.getKitInventoryLink(selectedSite);
        const eQueriesLink = this.getEQueriesLink(selectedSite);
        const eReqLink = this.getEreqLink(selectedSite);

        return [
            labReportsLink,
            ...documentsLinks,
            ...onScreenResultsLinks,
            subjectsLink,
            kitInventoryLink,
            eQueriesLink,
            eReqLink,
        ];
    }

    private getLabReportsLink(selectedSite: Site, siteSpecificCategories: Category[]): NavigationLink {
        const navigationPath = this.urlBuilder.esa().site(selectedSite);
        const labReportsUrl = navigationPath.labReports().url();
        const labReportsCategory = find(siteSpecificCategories, category => category.isLabReport());
        const isDisabled = this.isDocumentLinkDisabled(selectedSite, labReportsCategory);

        const navLink = new NavigationLink('Lab Reports', of(labReportsUrl), labReportsCategory.icon, this.getUnreadCountSource(selectedSite, labReportsCategory), null, isDisabled);
        return navLink;
    }

    private getDocumentsLinks(selectedSite: Site, siteSpecificCategories: Category[]): NavigationLink[] {
        const navigationPath = this.urlBuilder.esa().site(selectedSite);

        const categoryRoutes = siteSpecificCategories
            .filter(c => !c.isLabReport())
            .map(c => {
                const urlDocuments = navigationPath.documents(c);

                const url = urlDocuments.url();

                const isDisabled = this.isDocumentLinkDisabled(selectedSite, c);

                return new NavigationLink(c.name, of(url), c.icon, this.getUnreadCountSource(selectedSite, c), null, isDisabled, c.categoryId);
            });

        return categoryRoutes;
    }

    private getOnScreenResultsLinks(selectedSite: Site): NavigationLink[] {
        const onScreenResults = this.urlBuilder.esa().site(selectedSite).onScreenResults();
        const microbiology = onScreenResults.microbiology();

        const osrLinks = [
            new NavigationLink('Standard Test Results', of(onScreenResults.standardTestResults().url()), 'multiline_chart'),
            new NavigationLink('Microbiology Results', of(microbiology.isolateCultureData().url()), 'blur_circular')
        ];

        return osrLinks;
    }

    private getKitInventoryLink(selectedSite: Site): NavigationLink {
        const kitInventoryUrl = this.urlBuilder.esa().site(selectedSite).kitInventory().url();

        const navLink = new NavigationLink('Kit Inventory', of(kitInventoryUrl), 'card_travel');
        return navLink;
    }

    private getEreqLink(selectedSite: Site): NavigationLink {
        const eReqUrl = of([this.ereqLink.url + 'studies/' + selectedSite.ctSiteId]);

        const navLink = new NavigationLink('eRequisition', eReqUrl, 'open_in_new', null, null, null, null, null, null, true);
        return navLink;
    }

    private getEQueriesLink(selectedSite: Site): NavigationLink {
        const url = this.urlBuilder.esa().site(selectedSite).eQueries().url();
        const countersBadge = this.eQueriesCountersStorage.counters.pipe(
            map(counters => counters
                ? this.getEQueriesCounterBySite(counters, selectedSite)
                : '')
        );
        return new NavigationLink('eQueries', of(url), 'settings_input_antenna', countersBadge, null, of(false), 'e-queries');
    }

    private getEQueriesCounterBySite(counters: EQueryCounter[], selectedSite: Site): string {
        const counter = find(counters, c => c.key === selectedSite.siteId);
        const totalUnresolved = counter ? counter.totalUnresolved.size : 0;
        return totalUnresolved
            ? totalUnresolved.toString(10)
            : '';
    }

    private getUnreadCountSource(site: Site, category: Category): Observable<string> {
        return this.unreadReportsRegistry
            .allUnread
            .pipe(
                map((reports: InvestigatorDocumentsCounter[]) => {

                    const count = chain(reports)
                        .filter(x => x.siteId === site.siteId && x.rootCategoryId === category.categoryId)
                        .sumBy(x => x.totalUnread)
                        .value();

                    return count ? `${count.toString(10)}` : '';
                })
            );
    }

    private isDocumentLinkDisabled(site: Site, category: Category): Observable<boolean> {
        return this.unreadReportsRegistry
            .allUnread
            .pipe(
                map((reports: InvestigatorDocumentsCounter[]) => {

                    const total = chain(reports)
                        .filter(x => x.siteId === site.siteId && x.rootCategoryId === category.categoryId)
                        .sumBy(x => x.totalDocs)
                        .value();

                    return total === 0;
                })
            );
    }
}
